import { z } from "zod";

import {
  chatboxCharacterSchema,
  chatboxRadiusSchema,
  proactivePromptStyleSchema,
  settingsResponseSchema,
  supportRequestCapabilitiesSchema,
  type FeedbackDraft,
  type FeedbackReason,
  type FeedbackSnapshot,
} from "@go-novus/admin-api";

const widgetSpacingSchema = z.number().int().min(0).max(120);
export const voiceModeSchema = z.enum(["live", "cascade"]);
export type VoiceMode = z.infer<typeof voiceModeSchema>;

/**
 * 外观。商家只做四个决定（accent、surface、radius、direction），
 * 其余色值由挂件按主题预设或亮度派生；字体默认 inherit 宿主页面。
 * 全部可选：缺省时用设计稿给的默认值。
 */
export const widgetAppearanceSchema = z.object({
  accent: z.string().optional(),
  /** 图片地址或一到两个字母的字母头像。 */
  avatar: z.string().optional(),
  /** 面板毛玻璃的模糊半径，0 到 32。 */
  blur: z.number().min(0).max(32).optional(),
  character: chatboxCharacterSchema.optional(),
  direction: z.enum(["hairline", "editorial"]).optional(),
  font: z.string().optional(),
  fontSize: z.string().optional(),
  launcherLabel: z.string().optional(),
  /** 缺省按可拖动处理，和商家配置的默认值一致。 */
  launcherDraggable: z.boolean().optional(),
  /** 缺省不摆复制按钮，和商家配置的默认值一致。 */
  replyCopyButton: z.boolean().optional(),
  placeholder: z.string().optional(),
  position: z.enum(["left", "right"]).optional(),
  proactivePromptStyle: proactivePromptStyleSchema.optional(),
  horizontalSpacing: widgetSpacingSchema.optional(),
  bottomSpacing: widgetSpacingSchema.optional(),
  radius: chatboxRadiusSchema.optional(),
  subtitle: z.string().optional(),
  suggestions: z.array(z.string()).optional(),
  /** `light`、`dark`，或直接给店铺的背景色。 */
  surface: z.string().optional(),
  surfaceOpacity: z.number().min(0.4).max(1).optional(),
  title: z.string().optional(),
  /** 首屏问候语，对应商家后台 Settings 里的 welcomeMessage。 */
  welcomeMessage: z.string().optional(),
});

export type WidgetAppearance = z.infer<typeof widgetAppearanceSchema>;

/** 主题只覆盖定位；缺省、继承或无效值保留后台位置与默认边距。 */
export function readThemePlacement(
  data: DOMStringMap,
): Pick<WidgetAppearance, "position" | "horizontalSpacing" | "bottomSpacing"> {
  const position = widgetAppearanceSchema.shape.position.safeParse(
    data.widgetPosition,
  ).data;
  const horizontalSpacing = widgetSpacingSchema.safeParse(
    Number(data.horizontalSpacing?.trim() || undefined),
  ).data;
  const bottomSpacing = widgetSpacingSchema.safeParse(
    Number(data.bottomSpacing?.trim() || undefined),
  ).data;
  return {
    ...(position === undefined ? {} : { position }),
    ...(horizontalSpacing === undefined ? {} : { horizontalSpacing }),
    ...(bottomSpacing === undefined ? {} : { bottomSpacing }),
  };
}

/**
 * 换一份新令牌。后台没有 App Proxy 可走，挂件自己签不了，
 * 只能让宿主为同一个 uid 去要——uid 不变，页面上的问答才留得住。
 * 拿不到就返回 null，挂件按不可恢复处理。
 */
export type PlaygroundTokenRenewal = () => Promise<{
  socketUrl: string;
  token: string;
  capabilities: { support_requests: boolean };
} | null>;

/**
 * 已签发的 Playground 会话，只有后台注入。
 * `uid` 一定带 `test:` 前缀：不进生产 Inbox、不计生产指标；业务事件携带可信测试来源。
 * `token` 是短期的，过期后挂件用 `renew` 自己换，不需要重挂。
 */
/** 只有 Inbox 回放和 Playground 传入。店面不传，买家看不到反馈。 */
export interface MerchantFeedbackClient {
  align: "start" | "end";
  /** 点赞失败交给宿主通知区；反馈表单的错误仍显示在表单内。 */
  onLikeError?: (message: string) => void;
  loadReasons: () => Promise<FeedbackReason[]>;
  load: (messageIds: string[]) => Promise<FeedbackSnapshot[]>;
  submit: (
    messageId: string,
    draft: FeedbackDraft,
  ) => Promise<FeedbackSnapshot>;
}

/** 宿主已确认的网络异常；反馈界面据此显示连接提示，不展示原始错误。 */
export class MerchantFeedbackNetworkError extends Error {}

export const playgroundSessionSchema = z.object({
  uid: z.string().min(1),
  token: z.string().min(1),
  socketUrl: z.url(),
  settings: settingsResponseSchema,
  capabilities: supportRequestCapabilitiesSchema,
  renew: z.custom<PlaygroundTokenRenewal>(
    (value) => typeof value === "function",
  ),
});

/**
 * 挂载 Widget 时宿主注入的配置。
 * 店面由 Theme App Extension 的 liquid block 注入，Playground 由后台注入。
 */
export const widgetConfigSchema = z.object({
  appearance: widgetAppearanceSchema.default({}),
  /** App Proxy 的 Handshake 地址，例如 `/apps/gonovus-chat/handshake`。 */
  handshakeUrl: z.string().min(1),
  /** 可选的 audio-agent Gateway WebSocket 地址；未配置时不显示语音按钮。 */
  voiceGatewayUrl: z
    .string()
    .trim()
    .refine((value) => /^wss?:\/\//.test(value), {
      message: "必须是 ws:// 或 wss:// 地址",
    })
    .optional(),
  /** 语音 Gateway 的前端选择；缺省时由 Gateway 使用默认前端。 */
  voiceMode: voiceModeSchema.optional(),
  /**
   * 后台 Playground 注入的会话。后台里没有 App Proxy 可走，令牌和已保存配置
   * 由 `POST /api/playground/sessions` 直接下发；店面留空，两者都从握手取。
   */
  playgroundSession: playgroundSessionSchema.optional(),
  /** 店面入口显式开启购物车能力；Playground 和预览不提供。 */
  storefrontRootUrl: z
    .string()
    .regex(/^\/(?:[a-zA-Z0-9_-]+\/)*$/)
    .optional(),
  customerName: z.string().optional(),
  /**
   * Liquid 自报的 `customer.id`（数字串；确认未登录为空串），
   * 随登录同步与 Proxy 签名值核对，不能单独授予授权。
   */
  liquidCustomerId: z.string().regex(/^\d*$/).optional(),
  /** Liquid 自报的账户能力，只决定是否显示店铺登录入口。 */
  customerAccountsEnabled: z.boolean().optional(),
  /** Liquid 的 routes.storefront_login_url，登录回跳当前页面。 */
  accountLoginUrl: z.string().optional(),
  locale: z.string().min(1).optional(),
  shopDomain: z.string().min(1),
  merchantFeedback: z
    .custom<MerchantFeedbackClient>(
      (value) =>
        value != null &&
        typeof value === "object" &&
        typeof (value as MerchantFeedbackClient).loadReasons === "function" &&
        typeof (value as MerchantFeedbackClient).load === "function" &&
        typeof (value as MerchantFeedbackClient).submit === "function",
    )
    .optional(),
  /** 当前商品页的上下文，随下一条买家消息带给后端。 */
  productContext: z
    .object({
      id: z.string().min(1),
      title: z.string(),
      url: z.string().min(1),
      variantId: z.string().optional(),
    })
    .optional(),
});

/** 宿主注入的形状：`appearance` 可以整块省掉。 */
export type WidgetConfig = z.input<typeof widgetConfigSchema>;

/** 解析后的形状：`appearance` 一定在，组件不用再判空。 */
export type ResolvedWidgetConfig = z.output<typeof widgetConfigSchema>;

/**
 * 配置错误是宿主接入方的问题，不是买家能恢复的失败，所以直接抛。
 */
export function parseWidgetConfig(config: unknown): ResolvedWidgetConfig {
  const parsed = widgetConfigSchema.safeParse(config);
  if (!parsed.success) {
    throw new Error(
      `[@go-novus/widget] 配置无效: ${z.prettifyError(parsed.error)}`,
    );
  }
  return parsed.data;
}
